"use client";

import { useMemo, useRef } from "react";
import { useAuiState } from "@assistant-ui/react";
import {
  DOC_EDIT_ALL_TOOLS,
  DOC_EDIT_PHASE_TOOLS,
  TOOL_TO_DOC_EDIT_VARIANT,
  type DocEditVariant,
  type DocEditPhaseKey,
} from "@/lib/toolConstants";

export type PhaseStatus = "pending" | "active" | "done" | "error";

export type EditPhaseInfo = {
  key: DocEditPhaseKey;
  status: PhaseStatus;
  subLabel?: string;
  errorMessage?: string;
};

export type EditCycleState = {
  editId: string;
  variant: DocEditVariant;
  documentName?: string;
  phases: EditPhaseInfo[];
  activePhaseIndex: number;
  narration?: string;
  summary?: string;
  patchCount?: number;
  isComplete: boolean;
  isRunning: boolean;
  hasError: boolean;
  retryCount: number;
  isAbandoned: boolean;
};

export type DocumentEditFlowState = {
  isActive: boolean;
  cycles: EditCycleState[];
};

type ToolPartSnapshot = {
  toolName: string;
  state?: string;
  progressMessage?: string;
  status?: { type: string };
  result?: string;
};

type DocEditCyclePartSnapshot = {
  type: "data";
  name: "doc-edit-cycle";
  editId: string;
  variant: DocEditVariant;
  documentName?: string;
  narration?: string;
  summary?: string;
  patchCount?: number;
  status: "running" | "complete" | "error";
};

const EMPTY_CONTENT: readonly unknown[] = [];

function getDocumentEditSignature(content: readonly Record<string, unknown>[]): string {
  let signature = "";
  for (const part of content) {
    if (part.type === "tool-call" && typeof part.toolName === "string") {
      const toolName = part.toolName;
      if (!DOC_EDIT_ALL_TOOLS.has(toolName)) continue;
      const status = part.status as { type?: string } | undefined;
      signature += [
        "tool",
        part.toolCallId,
        toolName,
        part.state,
        status?.type,
        part.progressMessage,
        typeof part.result === "string" ? part.result : "",
      ].join(":");
      signature += ";";
    }
    if (part.type === "data" && (part as { name?: string }).name === "doc-edit-cycle") {
      signature += [
        "cycle",
        part.editId,
        part.variant,
        part.documentName,
        part.narration,
        part.summary,
        part.patchCount,
        part.status,
      ].join(":");
      signature += ";";
    }
  }
  return signature;
}

/**
 * Detects document edit flow cycles in the current assistant message.
 * Each get_*_skill call starts a new cycle. Phases are inferred from tool-call parts.
 * Narration/summary come from doc-edit-cycle UiParts (SSE-driven).
 */
export function useDocumentEditFlow(): DocumentEditFlowState {
  const stableContentRef = useRef<readonly unknown[]>(EMPTY_CONTENT);
  const signatureRef = useRef("");

  const content = useAuiState(({ message }) => {
    if (message.role !== "assistant") return null;
    const nextContent = (message.content ?? EMPTY_CONTENT) as readonly Record<string, unknown>[];
    const nextSignature = getDocumentEditSignature(nextContent);
    if (nextSignature !== signatureRef.current) {
      signatureRef.current = nextSignature;
      stableContentRef.current = nextContent;
    }
    return stableContentRef.current;
  });

  const statusType = useAuiState(({ message }) => message.status?.type);
  const messageIsTerminal = statusType != null && statusType !== "running";

  return useMemo(() => {
    if (!content || (content as readonly unknown[]).length === 0)
      return EMPTY_STATE;

    const toolParts: ToolPartSnapshot[] = [];
    const cycleParts: DocEditCyclePartSnapshot[] = [];

    for (const part of content as readonly Record<string, unknown>[]) {
      if (part.type === "tool-call" && typeof part.toolName === "string") {
        toolParts.push({
          toolName: part.toolName as string,
          state: part.state as string | undefined,
          progressMessage: part.progressMessage as string | undefined,
          status: part.status as { type: string } | undefined,
          result: typeof part.result === "string" ? part.result : undefined,
        });
      }
      if (part.type === "data" && (part as { name?: string }).name === "doc-edit-cycle") {
        cycleParts.push(part as unknown as DocEditCyclePartSnapshot);
      }
    }

    // Filter to doc-edit tools only
    const docTools = toolParts.filter((p) =>
      DOC_EDIT_ALL_TOOLS.has(p.toolName),
    );
    if (docTools.length === 0) return EMPTY_STATE;

    // Build cycles from tool-call sequence
    // Each get_*_skill tool starts a new cycle UNLESS it's the same variant as current (retry)
    const cycles: EditCycleState[] = [];
    let currentCycleTools: ToolPartSnapshot[] = [];
    let currentVariant: DocEditVariant | null = null;
    let currentRetryCount = 0;

    for (const tool of docTools) {
      const variant = TOOL_TO_DOC_EDIT_VARIANT[tool.toolName];
      if (variant) {
        if (currentVariant === variant && currentCycleTools.length > 0) {
          // Same variant retry — reset tools for this cycle, increment retry count
          currentRetryCount++;
          currentCycleTools = [tool];
        } else {
          // Different variant or first cycle — flush previous, start new
          if (currentVariant !== null && currentCycleTools.length > 0) {
            cycles.push(
              buildCycle(
                currentCycleTools,
                currentVariant,
                cycleParts,
                messageIsTerminal,
                cycles.length,
                currentRetryCount,
              ),
            );
          }
          currentVariant = variant;
          currentCycleTools = [tool];
          currentRetryCount = 0;
        }
      } else {
        currentCycleTools.push(tool);
      }
    }

    // Flush the last cycle
    if (currentVariant !== null && currentCycleTools.length > 0) {
      cycles.push(
        buildCycle(
          currentCycleTools,
          currentVariant,
          cycleParts,
          messageIsTerminal,
          cycles.length,
          currentRetryCount,
        ),
      );
    }

    // If no cycles were created but doc tools exist, create one "redo" cycle
    if (cycles.length === 0 && docTools.length > 0) {
      cycles.push(
        buildCycle(docTools, "re-edit", cycleParts, messageIsTerminal, 0, 0),
      );
    }

    return {
      isActive: cycles.length > 0,
      cycles,
    };
  }, [content, messageIsTerminal]);
}

function buildCycle(
  tools: ToolPartSnapshot[],
  variant: DocEditVariant,
  cycleParts: DocEditCyclePartSnapshot[],
  messageIsTerminal: boolean,
  index: number,
  retryCount: number = 0,
): EditCycleState {
  const editId = `cycle-${index}`;

  // Find matching SSE-driven cycle part (by variant + index heuristic or editId)
  const matchingCyclePart = cycleParts[index] ?? null;

  // Determine phase statuses from tools
  const phaseKeys: DocEditPhaseKey[] = [
    "preparing",
    "editing",
    "reviewing",
    "saving",
  ];
  const phases: EditPhaseInfo[] = [];
  let activePhaseIndex = -1;
  let hasError = false;

  for (let i = 0; i < phaseKeys.length; i++) {
    const key = phaseKeys[i];
    const phaseToolSet = DOC_EDIT_PHASE_TOOLS[key];
    const phaseTools = tools.filter((t) => phaseToolSet.has(t.toolName));

    if (phaseTools.length === 0) {
      // Skip "reviewing" phase if no approval tools are present
      if (key === "reviewing") continue;
      phases.push({ key, status: "pending" });
      continue;
    }

    const anyRunning = phaseTools.some(
      (t) =>
        !messageIsTerminal &&
        (t.state === "partial" ||
          t.state === "pending" ||
          t.status?.type === "running"),
    );
    const anyError = phaseTools.some(
      (t) =>
        t.state === "complete" &&
        (t.result?.includes("Error_ToolFailed") ||
          t.result?.includes("SAVE_FAILED") ||
          t.result?.includes("PATCH_FAILED")),
    );
    const allDone =
      phaseTools.every((t) => t.state === "complete") && !anyRunning;

    let status: PhaseStatus;
    let subLabel: string | undefined;
    let errorMessage: string | undefined;

    if (anyError) {
      status = "error";
      hasError = true;
      errorMessage = phaseTools
        .find(
          (t) =>
            t.result?.includes("Error_ToolFailed") ||
            t.result?.includes("FAILED"),
        )
        ?.result?.slice(0, 120);
    } else if (anyRunning) {
      status = "active";
      if (activePhaseIndex < 0) activePhaseIndex = phases.length;
      subLabel = phaseTools.find((t) => t.progressMessage)?.progressMessage;
    } else if (allDone) {
      status = "done";
    } else {
      status = "active";
      if (activePhaseIndex < 0) activePhaseIndex = phases.length;
    }

    phases.push({ key, status, subLabel, errorMessage });
  }

  // Backward fill: if phase N is done/active and phase N-1 is still pending, promote N-1 to done
  for (let i = phases.length - 1; i > 0; i--) {
    if (
      (phases[i].status === "done" || phases[i].status === "active") &&
      phases[i - 1].status === "pending"
    ) {
      phases[i - 1] = { ...phases[i - 1], status: "done" };
    }
  }

  // Terminal settlement: when message ended, active phases settle to done
  if (messageIsTerminal) {
    for (let i = 0; i < phases.length; i++) {
      if (phases[i].status === "active") {
        phases[i] = { ...phases[i], status: "done" };
        if (activePhaseIndex === i) activePhaseIndex = -1;
      }
    }
  }

  // Apply narration from SSE cycle part as sublabel on the active editing phase
  const narration = matchingCyclePart?.narration;
  if (narration) {
    const editingPhase = phases.find((p) => p.key === "editing");
    if (
      editingPhase &&
      (editingPhase.status === "active" || editingPhase.status === "done")
    ) {
      editingPhase.subLabel = narration;
    }
  }

  const isComplete = phases.every((p) => p.status === "done");
  const isRunning = phases.some((p) => p.status === "active");

  // A cycle is abandoned when the message ended but the flow never reached
  // editing or saving phases (only "preparing" tools were called).
  const isAbandoned = messageIsTerminal
    && !hasError
    && !isComplete
    && phases.every(p => p.key === "preparing" ? true : p.status === "pending");

  return {
    editId: matchingCyclePart?.editId ?? editId,
    variant,
    documentName: matchingCyclePart?.documentName,
    phases,
    activePhaseIndex,
    narration,
    summary: matchingCyclePart?.summary,
    patchCount: matchingCyclePart?.patchCount,
    isComplete,
    isRunning,
    hasError,
    retryCount,
    isAbandoned,
  };
}

const EMPTY_STATE: DocumentEditFlowState = {
  isActive: false,
  cycles: [],
};
